// RuntimeBlockHost — per-block dispatcher for the Live Preview.
//
// Reuses the existing runtime renderers from
// `pages/designers/page/renderers.tsx` for layout/visual blocks. Text-
// class blocks (heading, paragraph, list, todo, quote, code, divider)
// have no dedicated runtime renderer in that file, so this host renders
// them inline — matching the styling used by BlockNode at design time
// but without the design-time chrome.
//
// Each block is wrapped with `BlockChrome` to expose the delete
// affordance when the runtime policy allows. Editable text properties
// switch to `EditableText` when the runtime is in document mode and
// the block opts in via `documentEditable`.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle2 } from 'lucide-react'
import { substituteVars } from '@/pages/designers/page/actions/substitute'
import { applyBindings, resolveValueRef, type ResolveContext, type ResolveScope } from '@/pages/designers/page/bindings'
import { rowsFromEntry } from './dataBinding'
import { isVisible, meOf, withMe, deviceVisibilityClass } from '@/pages/designers/page/visibility'
import { usePageRuntime, usePageFieldErrors, usePageFieldRefs } from '@/pages/designers/page/actions/runtime'
import { PageVarsContext } from '@/pages/designers/page/renderers'
import { headingFontVars } from '@/pages/designers/page/theme'
import { StyledBlock, dimToCss } from '@twasta/page-runtime/ThemedPageShell'
import {
  HeroRenderer, CtaBannerRenderer, SpacerRenderer, ColumnsRenderer,
  CardRenderer, CardGridRenderer, FeatureGridRenderer, StatsRenderer, CountdownRenderer, TimelineRenderer,
  TestimonialRenderer, PricingRenderer, FaqRenderer, TabsRenderer, AccordionRenderer,
  LogoCloudRenderer, ImageRenderer, VideoRenderer, MapRenderer, GalleryRenderer,
  ButtonRenderer, ButtonGroupRenderer, NewsletterRenderer, ContactFormRenderer,
  CalloutRenderer,
} from '@/pages/designers/page/renderers'
import EmbedPreview from '@/pages/designers/page/EmbedPreview'
import { SignInPromptCard, type SignInRequired } from '@twasta/page-runtime/signInPrompt'
import NotepadEditor from '@/pages/designers/page/NotepadEditor'
import { RichContentRenderer } from '@twasta/page-runtime/RichContentRenderer'
import type { RichContentBlock, CollectionSearchBlock, CommentsBlock } from '@twasta/page-schema'
import { CollectionSearchRenderer, CommentsRenderer } from '@twasta/page-runtime/readerBlocks'
import { CartCheckoutRenderer, MyRecordsRenderer, SavedRecordsRenderer } from '@twasta/page-runtime/commerceBlocks'
import { ReviewsRenderer } from '@twasta/page-runtime/reviewsBlock'
import type { CartCheckoutBlock, MyRecordsBlock, ReviewsBlock, SavedRecordsBlock } from '@twasta/page-schema'
import EntityPickerRenderer from '@/livepreview/ecommerce/EntityPickerRenderer'
import NewCustomerFormRenderer from '@/livepreview/ecommerce/NewCustomerFormRenderer'
import ProductSelectionRenderer from '@/livepreview/ecommerce/ProductSelectionRenderer'
import ProductPreviewRenderer from '@/livepreview/ecommerce/ProductPreviewRenderer'
import OrderSummaryRenderer from '@/livepreview/ecommerce/OrderSummaryRenderer'
import OrderSubmitRenderer from '@/livepreview/ecommerce/OrderSubmitRenderer'
import PaymentCheckoutRenderer from '@/livepreview/ecommerce/PaymentCheckoutRenderer'
import PaymentSettingsRenderer from '@/livepreview/ecommerce/PaymentSettingsRenderer'
import type {
  EntityPickerBlock as EntityPickerBlockType,
  NewCustomerFormBlock as NewCustomerFormBlockType,
  ProductSelectionBlock as ProductSelectionBlockType,
  ProductPreviewBlock as ProductPreviewBlockType,
  OrderSummaryBlock as OrderSummaryBlockType,
  OrderSubmitBlock as OrderSubmitBlockType,
  PaymentCheckoutBlock as PaymentCheckoutBlockType,
  PaymentSettingsBlock as PaymentSettingsBlockType,
  FileUploadBlock as FileUploadBlockType,
} from '@/pages/designers/page/types'
import { EmbedDocStateContext, type EmbedDocState } from '@/livepreview/useEmbedRecords'
import type { EmbedRecord } from '@/livepreview/useEmbedRecords'
import type {
  AccordionBlock, Block, BlockDimension, BlockRepeat, BulletListBlock, CodeBlock, ColumnsBlock,
  CustomCodeBlock, EmbedConfig, FormBlock, HeadingBlock, InputGroupBlock, ListItem,
  NotepadBlock, NumberedListBlock, PageDataSource, PageInput, ParagraphBlock,
  QuoteBlock, ResolvedDataEntry, TableBlock, TabsBlock, TodoBlock, TodoItem,
  VariableBlock, VariableDisplayFormat, Viewport,
} from '@/pages/designers/page/types'
import type { DocumentBlock } from '@/api/documents'
import { useRuntime } from './RuntimeContext'
import { RepeatFrame, LiveRowsContext, type LaneInfo } from '@twasta/page-runtime/live/repeatFrame'
import { applyStyleRules, rulesNeedClock } from '@twasta/page-runtime/live/styleRules'
import { useNow } from '@twasta/page-runtime/live/clock'
import { TimerRenderer } from '@twasta/page-runtime/live/timerBlock'
import type { TimerBlock } from '@twasta/page-schema'
import { EditableText, BlockWrap, AddBlockRow } from './editAffordances'
import { BlockChromeContext } from './sectionChrome'
import { AuthFormRenderer, AuthMenuRenderer, ChatbotRenderer, NavMenuRenderer, columnTrack, EyebrowLabel, BreadcrumbRenderer, FilterBarRenderer,
  CartBadgeRenderer, CartLinesRenderer, CartSummaryRenderer, WishlistButtonRenderer, WishlistBadgeRenderer, WishlistLinesRenderer, useSiteLink, rewriteInternalHrefs, useClickDispatch, repeatRowSource, type ChatTurn,
  BentoGridRenderer, MarqueeRenderer, CounterRenderer, BeforeAfterRenderer,
  CarouselRenderer, TeamRenderer, StepsRenderer, ComparisonTableRenderer,
  SocialLinksRenderer, LottieRenderer, CookieConsentRenderer, FloatingButtonRenderer,
  columnsStackCss, containerLayout, listHasIcons, ListItemIcon, CustomHtmlRenderer, AuthMenuContext } from '@twasta/page-runtime/renderers'
import { FileUploadBlockRenderer } from '@twasta/page-runtime/FileUploadBlockRenderer'
import { BlockErrorBoundary } from '@twasta/page-runtime/BlockErrorBoundary'
import { CustomCodeBridgeHostContext, type CustomCodeBridgeCaps } from '@twasta/page-runtime/customCodeBridge'
import { makeEmbedUploader } from '@/livepreview/embedDataApi'
import { inlineRichHtml, hasRichHtml } from '@twasta/page-runtime/richtext'
import { useSiteAuth } from './SiteAuthContext'
import { livePreviewChat } from '@/livepreview/api'
import type {
  BreadcrumbBlock,
  FilterBarBlock,
  CartBadgeBlock, CartLinesBlock, CartSummaryBlock, WishlistButtonBlock, WishlistBadgeBlock, WishlistLinesBlock, AuthFormBlock, AuthMenuBlock, ChatbotBlock, NavMenuBlock } from '@twasta/page-schema'

/**
 * Entry the rendering tree sees per slot — template blocks plus
 * runtime-added blocks, minus tombstones, applied through the
 * per-scope `docOrder` override. Mirrors the Angular runtime's
 * `entriesFor` / `entriesForScope` shape.
 */
export interface BlockEntry {
  id: string
  block: Block
  source: 'template' | 'added'
}

/**
 * Compute the ordered visible entries for `scope`, given the slot's
 * template-block list. Reads `docOrder`, `deletedIds`, runtime-added
 * blocks, and persisted property overrides from the runtime.
 */
export function useEntriesFor(scope: string, templateBlocks: Block[]): BlockEntry[] {
  const runtime = useRuntime()
  return useMemo(() => {
    const tombstoned = runtime.deletedIds
    const byId = new Map<string, BlockEntry>()
    for (const t of templateBlocks) {
      if (!t || typeof t.id !== 'string') continue
      if (tombstoned.has(t.id)) continue
      const overlay = runtime.blockProps[t.id]
      const merged = overlay ? ({ ...t, ...overlay } as Block) : t
      byId.set(t.id, { id: t.id, block: merged, source: 'template' })
    }
    const added = runtime.childrenAddedUnder(scope === '__root__' ? null : scope)
    for (const a of added) {
      if (tombstoned.has(a.id)) continue
      byId.set(a.id, { id: a.id, block: docBlockToBlock(a), source: 'added' })
    }

    const override = runtime.docOrder[scope]
    if (Array.isArray(override) && override.length > 0) {
      const out: BlockEntry[] = []
      const seen = new Set<string>()
      for (const id of override) {
        if (seen.has(id)) continue
        const e = byId.get(id)
        if (e) { out.push(e); seen.add(id) }
      }
      // Append anything missing from the override (newly added blocks
      // that haven't reached the order list yet) in natural order so
      // they stay visible.
      for (const t of templateBlocks) {
        if (typeof t?.id !== 'string') continue
        if (tombstoned.has(t.id)) continue
        if (seen.has(t.id)) continue
        const overlay = runtime.blockProps[t.id]
        const merged = overlay ? ({ ...t, ...overlay } as Block) : t
        out.push({ id: t.id, block: merged, source: 'template' })
        seen.add(t.id)
      }
      for (const a of added) {
        if (tombstoned.has(a.id)) continue
        if (seen.has(a.id)) continue
        out.push({ id: a.id, block: docBlockToBlock(a), source: 'added' })
        seen.add(a.id)
      }
      return out
    }

    const out: BlockEntry[] = []
    for (const t of templateBlocks) {
      if (typeof t?.id !== 'string') continue
      if (tombstoned.has(t.id)) continue
      const overlay = runtime.blockProps[t.id]
      const merged = overlay ? ({ ...t, ...overlay } as Block) : t
      out.push({ id: t.id, block: merged, source: 'template' })
    }
    for (const a of added) {
      if (tombstoned.has(a.id)) continue
      out.push({ id: a.id, block: docBlockToBlock(a), source: 'added' })
    }
    return out
  }, [runtime.deletedIds, runtime.docOrder, runtime.blockProps, runtime.childrenAddedUnder, scope, templateBlocks])
}

export function docBlockToBlock(b: DocumentBlock): Block {
  return {
    id: b.id,
    type: b.type as Block['type'],
    ...(b.properties as Partial<Block>),
  } as Block
}

interface HostProps {
  block: Block
  /** Scope key the surrounding container uses for ordering / drops. */
  scope: string
}

// Repeater row scopes (innermost LAST), provided down the React tree so
// nested repeaters and item_field bindings see the enclosing rows.
const ScopeContext = createContext<ResolveScope[]>([])
export function useRowScopes(): ResolveScope[] { return useContext(ScopeContext) }

/** Mirror of the backend `correlation_key` so the runtime can look up
 *  baked `by[key]` rows for a correlated (nested) data source. Must stay
 *  byte-identical to app/services/page_data_binding.py:correlation_key. */
function computeCorrelationKey(source: PageDataSource | undefined, scopes: ResolveScope[]): string {
  if (!source) return ''
  const innermost = scopes.length ? scopes[scopes.length - 1].row : undefined
  const byAlias = (alias?: string) => {
    if (!alias) return innermost
    for (let i = scopes.length - 1; i >= 0; i--) if (scopes[i].alias === alias) return scopes[i].row
    return undefined
  }
  const refs = [
    ...(source.filters || []).flatMap((f) => [f.value, f.from, f.to]),
    source.record && 'value' in source.record ? source.record.value : undefined,
  ]
  const parts: string[] = []
  for (const r of refs) {
    if (r && r.source === 'item_field') {
      const row = byAlias(r.scope) || {}
      parts.push(`${r.scope || ''}.${r.name}=${(row as Record<string, unknown>)[r.name as string]}`)
    }
  }
  parts.sort()
  return parts.join('|')
}

function scopesToRecord(scopes: ResolveScope[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (scopes.length) out.__innermost__ = scopes[scopes.length - 1].row
  for (const s of scopes) if (s.alias) out[s.alias] = s.row
  return out
}

function sourceIsCorrelated(s: PageDataSource | undefined): boolean {
  if (!s) return false
  const refs = [
    ...(s.filters || []).flatMap((f) => [f.value, f.from, f.to]),
    s.record && 'value' in s.record ? s.record.value : undefined,
  ]
  return refs.some((r) => r && r.source === 'item_field')
}

/**
 * The variables a visibility `condition` is evaluated against — the SAME
 * source page text resolves `{{tokens}}` from (`PageVarsContext`: page
 * variables, `me.*`, action state, `{{<dataSource>.<COL>}}` / `._loaded`,
 * and inside a repeater its row), falling back to the runtime's own vars on
 * a host that provides no context. Evaluating against `runtime.vars` alone
 * left every data-source condition unresolved — and an unresolved condition
 * shows the block, so every state of a page showed at once.
 */
/**
 * The device a designer preview SIMULATES by narrowing its frame (the Preview
 * modal, the designer's preview iframe). When set, a block's `devices` rule is
 * decided here by `isVisible` against it, and the `pg-hide-*` classes are NOT
 * emitted: those are @media rules on the real window width, so in a phone-wide
 * frame on a desktop screen they hid "mobile only" blocks and showed "hide on
 * mobile" ones. Unset (published-site viewer, Run view, embeds) the page IS
 * the viewport, and the classes decide.
 */
export const SimulatedViewportContext = createContext<Viewport | undefined>(undefined)

export function useVisibilityVars(): (key: string) => unknown {
  const runtime = useRuntime()
  const pageVars = useContext(PageVarsContext)
  const me = meOf(useContext(AuthMenuContext))
  const withSession = useMemo(() => withMe(runtime.vars, me) as (k: string) => unknown,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `me` by value
    [runtime.vars, me.signedIn, me.email, me.name, me.roles])
  return useCallback((key: string): unknown => {
    const own = typeof pageVars === 'function'
      ? (pageVars as (k: string) => unknown)(key)
      : (pageVars as Record<string, unknown> | undefined)?.[key]
    return own !== undefined ? own : withSession(key)
  }, [pageVars, withSession])
}

export default function RuntimeBlockHost({ block, scope }: HostProps) {
  // An editing surface (website Make changes) wraps each block it knows;
  // absent ⇒ the block renders exactly as before.
  const chrome = useContext(BlockChromeContext)
  const body = <RuntimeBlockBody block={block} scope={scope} />
  return chrome ? <>{chrome(block, body)}</> : body
}

function RuntimeBlockBody({ block, scope }: HostProps) {
  const runtime = useRuntime()
  const scopes = useContext(ScopeContext)
  const visibilityVars = useVisibilityVars()
  const simulatedViewport = useContext(SimulatedViewportContext)

  // Pull persisted overrides for this block (document mode) and apply.
  const merged = useMemo(() => {
    const overrides = runtime.blockProps[block.id]
    return overrides ? ({ ...block, ...overrides } as Block) : block
  }, [block, runtime.blockProps])

  // Resolve dynamic-source bindings (page inputs / data fields / item
  // fields / expressions) against the current vars, data resources and
  // row scope, before rendering.
  const ctx: ResolveContext = useMemo(
    () => ({ inputs: runtime.vars, vars: runtime.vars, resources: runtime.resources, scopes }),
    [runtime.vars, runtime.resources, scopes],
  )
  const resolved = useMemo(() => applyBindings(merged, ctx), [merged, ctx])
  // A style rule that reads an age (`{{x|age_min}}`) is re-judged as time
  // passes; any other rule changes only when its data does.
  const now = useNow(rulesNeedClock(merged.styleRules) ? 15000 : false)
  // `styleRules` — the style this block wears given its data (row-aware
  // inside a repeat). A repeated template keeps its rules for each copy.
  const bound = useMemo(
    () => (resolved.repeat ? resolved : applyStyleRules(resolved, visibilityVars)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` re-judges age rules
    [resolved, visibilityVars, now],
  )

  // Visibility gate (end-user view). Reorganize mode shows everything so
  // authors can still see/manage blocks hidden by a rule.
  if (
    !runtime.reorganizeMode &&
    !isVisible(bound.visibility, { roles: runtime.userRoles, vars: visibilityVars, viewport: simulatedViewport })
  ) {
    return null
  }

  // Data-bound repetition: render the block's template once per row.
  if (bound.repeat && bound.repeat.source) {
    return <Repeater block={bound} scope={scope} parentScopes={scopes} />
  }

  return (
    <BlockWrap block={bound} scope={scope}>
      <StyledSizedBlock block={bound}>
        {/* The same net the designer canvas has: one block with a malformed
            shape shows a placeholder instead of unwinding the whole page.
            'author' tone — whoever is in the workbench can fix it. */}
        <BlockErrorBoundary blockId={bound.id} blockType={bound.type} tone="author">
          <BlockBody block={bound} />
        </BlockErrorBoundary>
      </StyledSizedBlock>
    </BlockWrap>
  )
}

/**
 * Per-block wrapper for the in-app preview.
 *
 * This was a hand-duplicated THIRD copy of the same wrapper logic that lived
 * in the shared `StyledBlock` and in the SSR renderer — the comment above it
 * even documented the drift. It is now a thin adapter: the shared component
 * owns style, responsive layers, hover states, animation, sizing, anchors and
 * tooltips, so a new wrapper capability reaches this surface for free.
 *
 * The one preview-specific bit is the click dispatcher: this surface HAS a
 * live page runtime, so a whole-block `on_click` intent really fires here.
 */
function StyledSizedBlock({ block, children }: { block: Block; children: React.ReactNode }) {
  const dispatch = useClickDispatch()
  const simulatedViewport = useContext(SimulatedViewportContext)
  return (
    <StyledBlock
      style={block.style}
      responsive={block.responsive}
      blockId={block.id}
      blockType={block.type}
      size={block.size}
      animation={block.animation}
      anchorId={block.anchorId}
      tooltip={block.tooltip}
      className={simulatedViewport ? undefined : deviceVisibilityClass(block.visibility)}
      on_click={block.on_click}
      onActivate={(click) => { void dispatch(click) }}
    >
      {children}
    </StyledBlock>
  )
}

/**
 * Render a repeated block: clone its template once per row of the bound
 * list data source, pushing each row onto the scope chain so `item_field`
 * bindings (and nested repeaters) resolve against it.
 */
function Repeater({ block, scope, parentScopes }: { block: Block; scope: string; parentScopes: ResolveScope[] }) {
  const runtime = useRuntime()
  // What `{{…}}` resolves to OUTSIDE this repeat. Each copy chains its own
  // row in front of it, so a nested repeat still reaches its parent's
  // alias and every block inside one keeps the resolver's own tokens
  // (`{{action.loading}}`, `{{inputs.x}}`).
  const outerVars = useContext(PageVarsContext)
  const rep = block.repeat as BlockRepeat
  const source = (runtime.page.dataSources || []).find((s) => s.id === rep.source)
  const correlated = sourceIsCorrelated(source)
  const correlationKey = useMemo(() => computeCorrelationKey(source, parentScopes), [source, parentScopes])
  const [fetched, setFetched] = useState<ResolvedDataEntry | null>(null)

  // Baked / page-scope resources for this source (deployment-time data or
  // an already-fetched page-scope runtime source).
  const entry = source ? (runtime.resources[`ds:${source.id}`] as ResolvedDataEntry | undefined) : undefined

  // Fetch live when needed. Deployment sources are baked on the deployed
  // runtime (use `entry`), but in the in-app preview nothing is baked, so
  // they must be fetched live too — otherwise repeats over deployment data
  // (e.g. a testimonial bound to a feedback table) render no rows.
  useEffect(() => {
    if (!source) return
    if (!correlated && entry) return            // page-scope already resolved
    if (source.bindingTime !== 'runtime' && !runtime.isPreview) return
    let cancelled = false
    const scopeArg = correlated ? scopesToRecord(parentScopes) : undefined
    runtime.fetchDataSource(source, undefined, scopeArg)
      .then((e) => { if (!cancelled) setFetched(e) })
      .catch(() => { /* swallowed in fetchDataSource */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, correlated, correlationKey, entry, correlated ? runtime.refreshTick : 0])

  const rows = useMemo<Record<string, unknown>[]>(() => {
    if (!source) return []
    if (entry && !(correlated && source.bindingTime === 'runtime')) {
      return rowsFromEntry(entry, entry.correlated ? correlationKey : undefined)
    }
    return rowsFromEntry(fetched || undefined)
  }, [source, entry, fetched, correlated, correlationKey])

  // Strip `repeat` from the per-row template so it doesn't recurse.
  // NOTE: this hook MUST stay above any early return — `source` can flip
  // from undefined → defined as the page model loads, and a conditional
  // hook would change the hook count between renders (React error #310).
  const template = useMemo(() => {
    const t = { ...block } as Block
    delete (t as { repeat?: unknown }).repeat
    return t
  }, [block])

  const pageRuntime = usePageRuntime()
  const select = rep.select
  const selVar = select?.var
  const selection = useMemo(() => (selVar
    ? { value: runtime.vars[selVar], set: (v: string) => runtime.setVar(selVar, v) }
    : undefined),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setVar is stable
  [selVar, selVar ? runtime.vars[selVar] : undefined])

  if (!source) return null

  const alias = rep.as || 'item'
  // Row cap — the same rule the static site (renderSite ServerRepeat) and
  // the downloaded runtime (BlockHost) apply.
  const max = rep.max ?? rep.pagination?.pageSize ?? rows.length
  // The layout (stack / grid / board / plane), selection and new-row pulse
  // are the shared RepeatFrame's — the same component the static site and
  // the downloaded runtime arrange their copies with.
  return (
    <LiveRowsContext.Provider value={runtime.liveRows}>
      <RepeatFrame
        blockId={block.id}
        repeat={rep}
        source={source}
        rows={rows.slice(0, max)}
        selection={selection}
        templateHasClick={!!template.on_click}
        interactive={!runtime.reorganizeMode}
        onDropToLane={rep.layout?.dropAction
          ? (row, lane) => {
              void pageRuntime.runActionByName(
                rep.layout!.dropAction!,
                repeatRowSource(alias, row, outerVars),
                { 'drop.lane': lane, drop: { lane } },
              )
            }
          : undefined}
        renderLaneHeader={rep.layout?.laneHeader?.length
          ? (lane: LaneInfo) => (
            <ScopeContext.Provider value={[...parentScopes, { alias: 'lane', row: lane.vars }]}>
              <PageVarsContext.Provider value={repeatRowSource('lane', lane.vars, outerVars)}>
                {rep.layout!.laneHeader!.map((hb) => (
                  <RuntimeBlockHost key={hb.id} block={{ ...hb, id: `${hb.id}__lane_${lane.value}` } as Block} scope={scope} />
                ))}
              </PageVarsContext.Provider>
            </ScopeContext.Provider>
          )
          : undefined}
        renderCopy={(row, _i, copyId) => {
          // One alias for both readers: the static site and the downloaded
          // runtime both default an unnamed repeat to `item`.
          const next: ResolveScope[] = [...parentScopes, { alias, row }]
          // The row has to reach BOTH readers. `ScopeContext` serves the
          // `bindings` (`item_field`); `PageVarsContext` serves every
          // `{{alias.COLUMN}}` token — a card's `/{{p.SLUG}}` link, an
          // add-to-cart payload, an action's `{{line._txn_id}}`.
          return (
            <ScopeContext.Provider value={next}>
              <PageVarsContext.Provider value={repeatRowSource(alias, row, outerVars)}>
                <RuntimeBlockHost block={{ ...template, id: copyId } as Block} scope={scope} />
              </PageVarsContext.Provider>
            </ScopeContext.Provider>
          )
        }}
      />
    </LiveRowsContext.Provider>
  )
}

function FileUploadBody({ block }: { block: FileUploadBlockType }) {
  const runtime = useRuntime()
  const pageRuntime = usePageRuntime()
  // Routes to the public site-doc endpoint in site mode, else the author
  // document store — same transport the transaction embeds use. Carries this
  // block's page-bound token so anonymous uploads authenticate.
  const refreshEmbedTokens = runtime.refreshEmbedTokens
  const uploadAccess = useMemo(() => {
    const da = runtime.dataAccess
    if (!da || da.mode !== 'site') return da
    return {
      ...da,
      embedToken: (block as { _embedToken?: string })._embedToken,
      onEmbedTokenExpired: refreshEmbedTokens
        ? async () => (await refreshEmbedTokens())?.[block.id] ?? null
        : undefined,
    }
  }, [runtime.dataAccess, refreshEmbedTokens, block])
  const uploadFn = useMemo(
    () => makeEmbedUploader(uploadAccess, runtime.project),
    [uploadAccess, runtime.project],
  )
  // Durable per-block persistence (smart-doc instances): the uploaded URL is
  // saved into this block's document properties (DocSync, flushed on unmount)
  // and restored from blockProps on reopen — so an attachment survives even
  // with no page variable bound.
  const persistedUrl = (() => {
    const v = runtime.blockProps[block.id]?.uploadedUrl
    return typeof v === 'string' ? v : undefined
  })()
  const onPersist = useCallback(
    (url: string) => runtime.saveProperties(block.id, { uploadedUrl: url }),
    [runtime, block.id],
  )
  return (
    <FileUploadBlockRenderer
      block={block}
      vars={runtime.vars}
      setVar={runtime.setVar}
      uploadFn={uploadFn}
      runAction={pageRuntime.runActionByName}
      persistedUrl={persistedUrl}
      onPersist={onPersist}
    />
  )
}

function BlockBody({ block }: { block: Block }) {
  const runtime = useRuntime()
  const editable = runtime.canEdit(block)

  switch (block.type) {
    // ─── Text-class ────────────────────────────────
    case 'heading': {
      const hb = block as HeadingBlock
      const body = <HeadingBody block={hb} editable={editable} />
      if (!hb.eyebrow) return body
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <EyebrowLabel text={hb.eyebrow} />{body}
        </div>
      )
    }
    case 'paragraph':
      return <ParagraphBody block={block as ParagraphBlock} editable={editable} />
    case 'bullet_list':
      return <ListBody block={block as BulletListBlock} kind="bullet" editable={editable} />
    case 'numbered_list':
      return <ListBody block={block as NumberedListBlock} kind="numbered" editable={editable} />
    case 'todo':
      return <TodoBody block={block as TodoBlock} editable={editable} />
    case 'quote':
      return <QuoteBody block={block as QuoteBlock} editable={editable} />
    case 'code':
      return <CodeBody block={block as CodeBlock} editable={editable} />
    case 'divider':
      return (
        <div style={{ padding: '6px 0' }}>
          <hr style={{ border: 'none', borderTop: '1px solid var(--pg-border, var(--color-border))', margin: 0 }} />
        </div>
      )

    // ─── Media ─────────────────────────────────────
    case 'image':       return <ImageRenderer block={block} />
    case 'video':       return <VideoRenderer block={block} />
    case 'map':         return <MapRenderer block={block} />
    case 'gallery':     return <GalleryRenderer block={block} />
    case 'logo_cloud':  return <LogoCloudRenderer block={block} />

    // ─── Layout / sections ─────────────────────────
    case 'hero':         return <HeroRenderer block={block} />
    case 'cta_banner':   return <CtaBannerRenderer block={block} />
    case 'spacer':       return <SpacerRenderer block={block} />
    case 'columns':      return <ColumnsWithChildren block={block} />
    case 'container':    return <ContainerWithChildren block={block} />
    case 'card':         return <CardRenderer block={block} />
    case 'card_grid':    return <CardGridRenderer block={block} />
    case 'feature_grid': return <FeatureGridRenderer block={block} />
    case 'stats':        return <StatsRenderer block={block} />
    case 'countdown':    return <CountdownRenderer block={block} />
    case 'timer':        return <TimerRenderer block={block as TimerBlock} />
    case 'testimonial':  return <TestimonialRenderer block={block} />
    case 'pricing':      return <PricingRenderer block={block} />
    case 'faq':          return <FaqRenderer block={block} />
    case 'tabs':         return <TabsWithChildren block={block} />
    case 'accordion':    return <AccordionWithChildren block={block} />
    case 'callout':      return <CalloutRenderer block={block} />
    case 'timeline':     return <TimelineRenderer block={block} />

    // ─── Marketing (shared page-runtime renderers) ──
    case 'bento_grid':       return <BentoGridRenderer block={block} />
    case 'marquee':          return <MarqueeRenderer block={block} />
    case 'counter':          return <CounterRenderer block={block} />
    case 'before_after':     return <BeforeAfterRenderer block={block} />
    case 'carousel':         return <CarouselRenderer block={block} />
    case 'team':             return <TeamRenderer block={block} />
    case 'steps':            return <StepsRenderer block={block} />
    case 'comparison_table': return <ComparisonTableRenderer block={block} />
    case 'social_links':     return <SocialLinksRenderer block={block} />
    case 'lottie':           return <LottieRenderer block={block} />
    case 'cookie_consent':   return <CookieConsentRenderer block={block} />
    case 'floating_button':  return <FloatingButtonRenderer block={block} />

    // ─── Action / form ─────────────────────────────
    case 'button':        return <ButtonRenderer block={block} />
    case 'button_group':  return <ButtonGroupRenderer block={block} />
    case 'newsletter':    return <NewsletterRenderer block={block} />
    case 'contact_form':  return <ContactFormRenderer block={block} />

    // ─── eCommerce (Proteus Vision) ────────────────
    case 'entity_picker':
      return <EntityPickerRenderer block={block as EntityPickerBlockType} />
    case 'new_customer_form':
      return <NewCustomerFormRenderer block={block as NewCustomerFormBlockType} />
    case 'product_selection':
      return <ProductSelectionRenderer block={block as ProductSelectionBlockType} />
    case 'product_preview':
      return <ProductPreviewRenderer block={block as ProductPreviewBlockType} />
    case 'order_summary':
      return <OrderSummaryRenderer block={block as OrderSummaryBlockType} />
    case 'order_submit':
      return <OrderSubmitRenderer block={block as OrderSubmitBlockType} />
    // The application's OWN checkout — this dispatch serves the in-app
    // preview, the Run view AND the published site (WebsiteViewerPage mounts
    // the same SmartPageRuntime), so wiring it here covers every surface.
    case 'payment_checkout':
      return <PaymentCheckoutRenderer block={block as PaymentCheckoutBlockType} />
    // Its counterpart: the screen that CAPTURES the configuration the block
    // above spends. Same dispatch, so an application's own settings page
    // works wherever its checkout does.
    case 'payment_settings':
      return <PaymentSettingsRenderer block={block as PaymentSettingsBlockType} />

    // ─── Embed (capture / live preview) ────────────
    case 'embed':
      return <EmbedBody block={block} />
    case 'file_upload':
      return <FileUploadBody block={block as FileUploadBlockType} />

    // ─── Data / form blocks ────────────────────────
    case 'form':
      return <FormBody block={block as FormBlock} />
    case 'table':
      return <TableBody block={block as TableBlock} />
    case 'input_group':
      return <InputGroupBody block={block as InputGroupBlock} />
    case 'variable':
      return <VariableBody block={block as VariableBlock} />
    case 'custom_code':
      return <CustomCodeBody block={block as CustomCodeBlock} />
    case 'custom_html':
      return <CustomCodeBody block={block as unknown as CustomCodeBlock} />
    case 'breadcrumb':
      return <BreadcrumbRenderer block={block as BreadcrumbBlock} />
    case 'filter_bar':
      return <FilterBarRenderer block={block as FilterBarBlock} />
    case 'cart_badge':
      return <CartBadgeRenderer block={block as CartBadgeBlock} />
    case 'cart_lines':
      return <CartLinesRenderer block={block as CartLinesBlock} />
    case 'wishlist_button':
      return <WishlistButtonRenderer block={block as WishlistButtonBlock} />
    case 'wishlist_badge':
      return <WishlistBadgeRenderer block={block as WishlistBadgeBlock} />
    case 'wishlist_lines':
      return <WishlistLinesRenderer block={block as WishlistLinesBlock} />
    case 'cart_summary':
      return <CartSummaryRenderer block={block as CartSummaryBlock} />
    case 'nav_menu':
      return <NavMenuRenderer block={block as NavMenuBlock} />

    // ─── Chatbot ───────────────────────────────────
    case 'chatbot':
      return <ChatbotBody block={block as ChatbotBlock} />

    // ─── Auth ──────────────────────────────────────
    case 'auth_form':
      return <AuthFormBody block={block as AuthFormBlock} />
    // Reads the package AuthMenuContext (provided by the Site viewer); absent a
    // provider (plain in-app Live Preview) it renders the signed-out buttons.
    case 'auth_menu':
      return <AuthMenuRenderer block={block as AuthMenuBlock} />

    // ─── Other v2 blocks — best-effort stub ────────
    case 'notepad':
      return <NotepadBody block={block as NotepadBlock} editable={editable} />
    case 'rich_content':
      return <RichContentRenderer block={block as RichContentBlock} vars={runtime.vars} />
    case 'collection_search':
      return <CollectionSearchRenderer block={block as CollectionSearchBlock} />
    case 'comments':
      return <CommentsRenderer block={block as CommentsBlock} vars={runtime.vars} />
    // Storefront commerce blocks — their server comes from the
    // PageDataBlocksContext SmartPageRuntime provides (site doors or the
    // Run-view twins). The design canvas renders them with `design`.
    case 'cart_checkout':
      return <CartCheckoutRenderer block={block as CartCheckoutBlock} setVar={runtime.setVar} />
    case 'my_records':
      return <MyRecordsRenderer block={block as MyRecordsBlock} />
    case 'saved_records':
      return <SavedRecordsRenderer block={block as SavedRecordsBlock} />
    case 'reviews':
      return <ReviewsRenderer block={block as ReviewsBlock} vars={runtime.vars} />
    default:
      return (
        <div
          style={{
            padding: 12,
            border: '1px dashed var(--color-border)',
            borderRadius: 6,
            color: 'var(--color-text-secondary)',
            fontSize: 12,
          }}
        >
          Block type "{(block as { type?: string }).type}" is not yet supported in Live Preview.
        </div>
      )
  }
}

// ─── Chatbot body ─────────────────────────────────
// Renders a `chatbot` block in the in-app Live Preview. Each turn is sent to
// the backend /livepreview/chat endpoint, which grounds the LLM on the page
// text (extracted client-side from the page model the preview already holds)
// plus a bounded sample of records from the configured data sources, using
// the author's twasta LLM settings. Mirrors the deployed runtime's wiring
// (runtime/client .../BlockHost.tsx) but against the preview backend.

const CHAT_TEXT_KEYS = new Set([
  'text', 'title', 'subtitle', 'heading', 'subheading', 'body', 'label',
  'description', 'question', 'answer', 'quote', 'caption', 'eyebrow', 'value',
])

function collectPageText(node: unknown, out: string[]): void {
  if (Array.isArray(node)) {
    for (const item of node) collectPageText(item, out)
  } else if (node && typeof node === 'object') {
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (CHAT_TEXT_KEYS.has(k) && typeof v === 'string') out.push(v)
      else collectPageText(v, out)
    }
  }
}

function extractPageText(page: unknown): string {
  const out: string[] = []
  collectPageText(page, out)
  const seen: string[] = []
  for (const raw of out) {
    const s = raw.trim()
    if (s && (seen.length === 0 || seen[seen.length - 1] !== s)) seen.push(s)
  }
  return seen.join('\n')
}

function ChatbotBody({ block }: { block: ChatbotBlock }) {
  const runtime = useRuntime()
  const onSend = useCallback(
    async (text: string, history: ChatTurn[]) => {
      const groundOnPage = block.groundOnPage !== false
      const resp = await livePreviewChat(runtime.project, {
        messages: [...history, { role: 'user', content: text }],
        page_text: groundOnPage ? extractPageText(runtime.page) : undefined,
        obj_names: block.dataSources || [],
        system_prompt: block.systemPrompt,
      })
      return resp.reply
    },
    [block, runtime.project, runtime.page],
  )
  return <ChatbotRenderer block={block} onSend={onSend} />
}

// ─── Auth form body ───────────────────────────────
// Renders an `auth_form` block. When the Site preview provides a SiteAuthContext
// (a real end-user login flow), the form submits for real; otherwise it's a
// visual-only form (designer canvas / in-app preview / SSR), exactly as before.
function AuthFormBody({ block }: { block: AuthFormBlock }) {
  const adapter = useSiteAuth()
  return (
    <AuthFormRenderer
      block={block}
      onSubmit={adapter?.onSubmit}
      twastaSignin={adapter?.twastaSignin}
      initialMode={adapter?.initialMode}
    />
  )
}

// ─── Embed body ───────────────────────────────────
// Hosts an embed block in the smart-page runtime. In document mode it
// also provides the EmbedDocStateContext that useEmbedRecords reads,
// so capture-mode templates can persist their unsynced rows into the
// block's per-document properties and restore them on reopen.

// The sign-in card's words for an embedded wizard (a per-embed
// `signInPrompt` still wins).
const WIZARD_SIGN_IN_COPY = {
  title: 'Sign in to continue',
  body: 'This step keeps your progress so you can come back to it. Sign in, or create an account, to carry on.',
}

function EmbedBody({ block }: { block: Block }) {
  const runtime = useRuntime()

  // Saved unsynced records (if any) live under `properties.captureState`
  // on the embed's per-document instance. Pull them straight off the
  // merged block — they were already overlaid by RuntimeBlockHost.
  const captureState = (block as { captureState?: { records?: EmbedRecord[] } }).captureState
  const initialRecords = useMemo(
    () => (Array.isArray(captureState?.records) ? captureState!.records : undefined),
    [captureState],
  )

  // Stable writer so useEmbedRecords' onRecordsChange dep doesn't flip
  // on every render. saveProperties already debounces via DocSync.
  const blockId = block.id
  const saveProperties = runtime.saveProperties
  const onRecordsChange = useCallback(
    (records: EmbedRecord[]) => {
      saveProperties(blockId, { captureState: { records } })
    },
    [blockId, saveProperties],
  )

  // Only wire the doc-state bridge inside a real document. Preview /
  // designer canvases pass through with no persistence so the hook
  // behaves exactly as it does today.
  const docState = useMemo<EmbedDocState | null>(
    () => runtime.mode === 'document' ? { initialRecords, onRecordsChange } : null,
    [runtime.mode, initialRecords, onRecordsChange],
  )

  const embedBlock = block as Extract<Block, { type: 'embed' }>
  const embed = embedBlock.embed
  // Honor an author-set block height: a visual/dashboard embed fills it instead
  // of content-sizing. Undefined → the embed sizes to its content.
  const bodyHeight = dimToCss(embedBlock.size?.height)

  // Per-block DataAccess carrying THIS embed's page-bound token so anonymous
  // site-txn / site-doc calls authenticate as coming from this embed. A 401
  // EMBED_TOKEN_EXPIRED triggers a page-model re-fetch for a fresh token.
  const refreshEmbedTokens = runtime.refreshEmbedTokens
  // Resolve THIS embed's scope-filter values (page_input / static) against the
  // current page vars, keyed by filter id ({fid: {value}} / {fid: {from,to}}).
  // A page-bound edit-mode embed must echo these on every site-txn call so the
  // server can enforce the designer's row scope (list / execute / batch).
  // Undefined when the embed has no filters, so non-scoped embeds send nothing.
  // Compute to a STABLE string first: `runtime.vars` gets a fresh object
  // reference on every var change, so returning a fresh object here would make
  // `perBlockAccess` / `dataAccess` change identity every render and send the
  // records hook into a refetch loop. Deriving the object from a content
  // signature keeps its reference stable whenever the resolved values are.
  const embedFilterValuesSig = useMemo(() => {
    const filters = embed?.filters
    if (!filters || filters.length === 0) return ''
    const out: Record<string, { value?: unknown; from?: unknown; to?: unknown }> = {}
    for (const f of filters) {
      const fid = String(f.id || f.column || '').trim()
      if (!fid) continue
      out[fid] = f.op === 'between'
        ? { from: resolveValueRef(f.from, { vars: runtime.vars }), to: resolveValueRef(f.to, { vars: runtime.vars }) }
        : { value: resolveValueRef(f.value, { vars: runtime.vars }) }
    }
    return Object.keys(out).length ? JSON.stringify(out) : ''
  }, [embed, runtime.vars])
  const embedFilterValues = useMemo(
    () => (embedFilterValuesSig
      ? (JSON.parse(embedFilterValuesSig) as Record<string, unknown>)
      : undefined),
    [embedFilterValuesSig],
  )

  // The server refused this embed's data because it belongs to ACCOUNTS and
  // none usable is in force (SIGN_IN_REQUIRED / SESSION_EXPIRED /
  // PROJECT_USER_REQUIRED). The embed is replaced by the shared sign-in card
  // rather than left as a dead, empty grid. Cleared when the viewer's roles
  // change — i.e. they signed in on this same page (an inline auth_form).
  const [signIn, setSignIn] = useState<SignInRequired | null>(null)
  const rolesKey = (runtime.userRoles || []).join(',')
  useEffect(() => { setSignIn(null) }, [rolesKey])

  const perBlockAccess = useMemo(() => {
    const da = runtime.dataAccess
    if (!da || da.mode !== 'site') return da
    return {
      ...da,
      embedToken: (embed as { _embedToken?: string } | undefined)?._embedToken,
      onEmbedTokenExpired: refreshEmbedTokens
        ? async () => (await refreshEmbedTokens())?.[blockId] ?? null
        : undefined,
      onSignInRequired: setSignIn,
      embedFilterValues,
    }
  }, [runtime.dataAccess, refreshEmbedTokens, embed, blockId, embedFilterValues])

  // Renders the actual embed body, optionally notified on a successful
  // save (used by the modal presentation to confirm + auto-close).
  const renderEmbed = (onSaved?: () => void) => {
    if (signIn) {
      // A wizard asks for a sign-in to keep the visitor's progress (its run
      // store / external steps), not to show records — say so by default.
      const prompt = embedBlock.signInPrompt
        ?? (embed?.obj_type === 'U' && signIn.code === 'SIGN_IN_REQUIRED' ? WIZARD_SIGN_IN_COPY : undefined)
      return <SignInPromptCard refusal={signIn} prompt={prompt} />
    }
    const node = (
      <EmbedPreview
        project={runtime.project}
        embed={embed}
        inputs={runtime.page.inputs}
        inputValues={runtime.vars}
        dataAccess={perBlockAccess}
        onSetInput={(name, value) => runtime.setVar(name, value)}
        onSaved={onSaved}
        surface="runtime"
        bodyHeight={bodyHeight}
      />
    )
    if (!docState) return node
    return (
      <EmbedDocStateContext.Provider value={docState}>
        {node}
      </EmbedDocStateContext.Provider>
    )
  }

  // Modal presentation: render a trigger button; the form opens in a
  // dialog. Ideal for "Add feedback" / "Register" actions that should
  // not occupy the page until invoked.
  if (embed?.presentation === 'modal') {
    return <EmbedModalLauncher embed={embed} renderBody={renderEmbed} />
  }

  // Containment: the embed sits in the page's flex/grid column. Give it
  // `minWidth: 0` so a wide child (a transaction grid once its data loads)
  // can shrink below its content width instead of forcing the column — and
  // the whole page — to scroll horizontally. `maxWidth: 100%` caps it at the
  // column; the embed's own table surface already scrolls internally
  // (ListView → SplitScrollTable / tabular overflow box).
  // NOTE: no `height` here on purpose — the block must stay exactly as tall as
  // its content. An explicit block height is threaded straight to the visual via
  // `bodyHeight`; a stretchy `height:100%` here let the grid/flex stretch the
  // block taller than its content on reflow, leaving blank space below it.
  return <div style={{ minWidth: 0, maxWidth: '100%' }}>{renderEmbed()}</div>
}

// ─── Modal embed launcher ─────────────────────────────
// A trigger button that opens the embed body (typically a capture
// add-form) in a centered modal dialog. On a successful save it shows a
// confirmation and auto-closes.

const TRIGGER_STYLES: Record<
  NonNullable<EmbedConfig['triggerVariant']>,
  React.CSSProperties
> = {
  primary: { background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: '1px solid var(--pg-primary)' },
  secondary: { background: 'var(--pg-surface)', color: 'var(--pg-text)', border: '1px solid var(--pg-border)' },
  outline: { background: 'transparent', color: 'var(--pg-primary)', border: '1px solid var(--pg-primary)' },
  ghost: { background: 'transparent', color: 'var(--pg-primary)', border: '1px solid transparent' },
}

function EmbedModalLauncher({
  embed,
  renderBody,
}: {
  embed: EmbedConfig
  renderBody: (onSaved?: () => void) => React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)
  const label = embed.triggerLabel
    || (embed.obj_type === 'U' ? 'Start'
      : embed.mode === 'add' ? 'Add' : embed.mode === 'edit' ? 'Edit' : 'Open')
  const title = embed.modalTitle || label
  const successMessage = embed.successMessage || 'Thank you! Your submission was received.'

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const close = useCallback(() => { setOpen(false); setDone(false) }, [])

  const handleSaved = useCallback(() => {
    setDone(true)
    // Give the user a moment to read the confirmation, then close.
    window.setTimeout(() => { setOpen(false); setDone(false) }, 1800)
  }, [])

  // Wrap so the button sizes to its content (the block slot is a
  // full-width flex item that would otherwise stretch the button).
  const trigger = (
    <div style={{ textAlign: 'center' }}>
      <button
        type="button"
        onClick={() => { setDone(false); setOpen(true) }}
        style={{
          ...(TRIGGER_STYLES[embed.triggerVariant || 'primary']),
          display: 'inline-block', width: 'auto',
          padding: '10px 20px', borderRadius: 8, fontWeight: 600,
          fontSize: '0.95rem', cursor: 'pointer',
        }}
      >
        {label}
      </button>
    </div>
  )

  if (!open) return trigger

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '5vh 16px', overflowY: 'auto',
      }}
    >
      <div
        style={{
          background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, #0f172a)',
          borderRadius: 12, width: '100%', maxWidth: 720,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          border: '1px solid var(--pg-border, #e2e8f0)',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--pg-border, #e2e8f0)',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h3>
          <button
            type="button" onClick={close} aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', lineHeight: 0 }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto' }}>
          {done ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 12, padding: '32px 16px', textAlign: 'center',
            }}>
              <CheckCircle2 size={40} style={{ color: 'var(--pg-primary, #16a34a)' }} />
              <p style={{ margin: 0, fontSize: '1rem' }}>{successMessage}</p>
            </div>
          ) : (
            renderBody(handleSaved)
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {trigger}
      {createPortal(modal, document.body)}
    </>
  )
}

// ─── Text-class implementations ────────────────────

function HeadingBody({ block, editable }: { block: HeadingBlock; editable: boolean }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const Tag = (`h${block.level || 1}`) as 'h1' | 'h2' | 'h3'
  const text = substituteVars(block.text, textVars) || ''
  // Theme-driven heading typography (size from --pg-h1/2/3 by level) — matches
  // the design canvas and the deployed/SSR surfaces. Was hardcoded rem.
  const headingStyle = headingFontVars(block.level || 1)
  const color = block.style?.textColor || 'var(--pg-heading, var(--pg-text, #0f172a))'

  if (editable) {
    return (
      <EditableText
        value={block.text || ''}
        displayValue={text}
        onCommit={(next) => runtime.saveProperties(block.id, hasRichHtml(block.html) ? { text: next, html: '' } : { text: next })}
        style={{ ...headingStyle, color }}
      />
    )
  }
  return (
    <Tag
      className="pg-rich"
      style={{ margin: 0, ...headingStyle, color }}
      dangerouslySetInnerHTML={{ __html: inlineRichHtml({ text: block.text, html: block.html, vars: textVars }) }}
    />
  )
}

function ParagraphBody({ block, editable }: { block: ParagraphBlock; editable: boolean }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const text = substituteVars(block.text, textVars) || ''
  const color = block.style?.textColor || 'var(--pg-text, #0f172a)'
  // A paragraph with its own size (block typography, applied on the wrapper)
  // inherits it, as on the published site; otherwise the preview's default.
  // Its size per breakpoint arrives as `--pg-text-size` (blockStyleCss).
  const fontSize = 'var(--pg-text-size, 0.95rem)'

  if (editable) {
    return (
      <EditableText
        value={block.text || ''}
        displayValue={text}
        multiline
        placeholder="Write something…"
        onCommit={(next) => runtime.saveProperties(block.id, hasRichHtml(block.html) ? { text: next, html: '' } : { text: next })}
        style={{ fontSize, lineHeight: 1.5, color }}
      />
    )
  }
  return (
    <p
      className="pg-rich"
      style={{ margin: 0, fontSize, lineHeight: 1.5, color }}
      dangerouslySetInnerHTML={{ __html: inlineRichHtml({ text: block.text, html: block.html, vars: textVars }) }}
    />
  )
}

function ListBody({
  block, kind, editable,
}: {
  block: BulletListBlock | NumberedListBlock
  kind: 'bullet' | 'numbered'
  editable: boolean
}) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const items: ListItem[] = block.items && block.items.length
    ? block.items
    : block.text ? [{ id: 'legacy', text: block.text }] : []
  const Tag = kind === 'bullet' ? 'ul' : 'ol'
  const iconic = listHasIcons(items)

  const commit = (nextItems: ListItem[]) => {
    runtime.saveProperties(block.id, { items: nextItems })
  }

  return (
    // The marker is explicit: the app's global reset sets `list-style: none`
    // on every ul/ol, which left a published list as bare indented lines.
    <Tag style={{ margin: 0, paddingLeft: iconic ? 0 : 24, listStyle: iconic ? 'none' : (kind === 'bullet' ? 'disc' : 'decimal'), lineHeight: 1.6, color: 'var(--pg-text, #0f172a)' }}>
      {items.map((item, idx) => (
        <li key={item.id} style={{ marginBottom: 2 }}>
          {iconic && item.iconUrl ? <ListItemIcon url={item.iconUrl} /> : null}
          {editable ? (
            <EditableText
              value={item.text}
              displayValue={substituteVars(item.text, textVars)}
              onCommit={(next) => {
                const copy = items.slice()
                copy[idx] = { ...item, text: next }
                commit(copy)
              }}
              style={{ fontSize: '0.95rem' }}
            />
          ) : (
            substituteVars(item.text, textVars)
          )}
        </li>
      ))}
    </Tag>
  )
}

function TodoBody({ block, editable }: { block: TodoBlock; editable: boolean }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const items: TodoItem[] = block.items || []
  // Reorganize mode reveals add/remove affordances; in read mode the
  // user can still tick checkboxes and edit text on documentEditable
  // blocks, but cannot mutate the list shape.
  const reorganize = runtime.reorganizeMode && editable

  const commit = (nextItems: TodoItem[]) => {
    runtime.saveProperties(block.id, { items: nextItems })
  }

  const insertAfter = (idx: number) => {
    const next: TodoItem = { id: makeTodoId(), text: '', checked: false }
    const copy = items.slice()
    copy.splice(idx + 1, 0, next)
    commit(copy)
  }

  const removeAt = (idx: number) => {
    const copy = items.slice()
    copy.splice(idx, 1)
    commit(copy)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map((item, idx) => (
        <div
          key={item.id}
          style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.95rem' }}
        >
          <input
            type="checkbox"
            checked={item.checked}
            disabled={!editable}
            onChange={(e) => {
              const copy = items.slice()
              copy[idx] = { ...item, checked: e.currentTarget.checked }
              commit(copy)
            }}
            style={{ flexShrink: 0 }}
          />
          {editable ? (
            <EditableText
              value={item.text}
              displayValue={substituteVars(item.text, textVars)}
              onCommit={(next) => {
                const copy = items.slice()
                copy[idx] = { ...item, text: next }
                commit(copy)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  ;(e.currentTarget as HTMLDivElement).blur()
                  insertAfter(idx)
                } else if (e.key === 'Backspace' && (e.currentTarget.textContent ?? '') === '') {
                  e.preventDefault()
                  removeAt(idx)
                }
              }}
              style={{
                flex: 1,
                textDecoration: item.checked ? 'line-through' : 'none',
                color: item.checked ? 'var(--color-text-secondary)' : 'inherit',
              }}
            />
          ) : (
            <span style={{
              flex: 1,
              textDecoration: item.checked ? 'line-through' : 'none',
              color: item.checked ? 'var(--color-text-secondary)' : 'inherit',
            }}>
              {substituteVars(item.text, textVars)}
            </span>
          )}
          {reorganize && (
            <button
              type="button"
              aria-label="Remove item"
              onClick={() => removeAt(idx)}
              style={{
                width: 22, height: 22, borderRadius: 4,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: '1px solid var(--color-border)',
                color: 'var(--color-text-secondary)', cursor: 'pointer',
                fontSize: 14, lineHeight: 1, flexShrink: 0,
              }}
            >×</button>
          )}
        </div>
      ))}
      {editable && (
        <button
          type="button"
          onClick={() => insertAfter(items.length - 1)}
          style={{
            alignSelf: 'flex-start',
            marginTop: 4,
            padding: '4px 8px',
            background: 'transparent',
            color: 'var(--color-text-secondary)',
            border: '1px dashed var(--color-border)',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          + Add to-do
        </button>
      )}
    </div>
  )
}

function makeTodoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `todo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function QuoteBody({ block, editable }: { block: QuoteBlock; editable: boolean }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const baseStyle: React.CSSProperties = {
    margin: 0,
    borderLeft: '3px solid var(--pg-primary, var(--color-primary))',
    paddingLeft: 12,
    fontStyle: 'italic',
    color: 'var(--pg-text, #1e293b)',
  }
  if (editable) {
    return (
      <blockquote style={baseStyle}>
        <EditableText
          value={block.text || ''}
          displayValue={substituteVars(block.text, textVars)}
          multiline
          onCommit={(next) => runtime.saveProperties(block.id, hasRichHtml(block.html) ? { text: next, html: '' } : { text: next })}
        />
      </blockquote>
    )
  }
  return (
    <blockquote
      className="pg-rich"
      style={baseStyle}
      dangerouslySetInnerHTML={{ __html: inlineRichHtml({ text: block.text, html: block.html, vars: textVars }) }}
    />
  )
}

function CodeBody({ block, editable }: { block: CodeBlock; editable: boolean }) {
  const runtime = useRuntime()
  return (
    <div style={{
      background: 'var(--pg-surface, var(--color-surface))',
      border: '1px solid var(--pg-border, var(--color-border))',
      borderRadius: 6,
      padding: 10,
      fontFamily: 'monospace',
      fontSize: '0.85rem',
      position: 'relative',
    }}>
      {block.language && (
        <div style={{
          position: 'absolute', right: 8, top: 4,
          fontSize: '0.75rem', color: 'var(--pg-text-secondary, var(--color-text-secondary))',
          textTransform: 'uppercase',
        }}>{block.language}</div>
      )}
      {editable ? (
        <EditableText
          value={block.text || ''}
          multiline
          onCommit={(next) => runtime.saveProperties(block.id, { text: next })}
          style={{ whiteSpace: 'pre' }}
        />
      ) : (
        <pre style={{ margin: 0, color: 'var(--pg-text, #0f172a)' }}>{block.text}</pre>
      )}
    </div>
  )
}

function NotepadBody({ block, editable }: { block: NotepadBlock; editable: boolean }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const { resolveHref } = useSiteLink()
  const minHeight = block.minHeight ?? 160

  if (editable) {
    return (
      <NotepadEditor
        block={block}
        onSelect={() => { /* selection handled by RuntimeBlockHost wrapper */ }}
        onChange={(next) => {
          // NotepadEditor emits the full block; persist only the bits
          // the runtime cares about (html, text, optional minHeight).
          const patch: Record<string, unknown> = {}
          if ('html' in next) patch.html = (next as NotepadBlock).html
          if ('text' in next) patch.text = (next as NotepadBlock).text
          if ('minHeight' in next) patch.minHeight = (next as NotepadBlock).minHeight
          runtime.saveProperties(block.id, patch)
        }}
      />
    )
  }

  // Read mode: render saved HTML with variable substitution. Fall back
  // to a wrapped <p> when only `text` was authored so the block never
  // renders as an invisible empty surface.
  let raw = block.html ?? ''
  if (!raw) {
    const text = (block as { text?: string }).text || ''
    if (text) raw = `<p>${escapeHtml(text)}</p>`
  }
  const interpolated = substituteVars(raw, textVars)
  const safe = rewriteInternalHrefs(stripScripts(interpolated), resolveHref)

  if (!safe) {
    return (
      <div style={{
        minHeight, padding: 12, borderRadius: 6,
        background: 'var(--pg-surface, var(--color-surface))',
        border: '1px solid var(--pg-border, var(--color-border))',
        color: 'var(--color-text-secondary)', fontStyle: 'italic',
      }}>
        (empty notepad)
      </div>
    )
  }

  return (
    <div
      className="pg-notepad"
      style={{
        minHeight,
        padding: '12px 14px',
        borderRadius: 8,
        background: 'var(--pg-surface, var(--color-surface))',
        border: '1px solid var(--pg-border, var(--color-border))',
        color: 'var(--pg-text, var(--color-text))',
        lineHeight: 1.6,
        fontSize: '0.95rem',
      }}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  )
}

// Defense-in-depth: notepad HTML is authored in a sanitised
// contentEditable surface, but documents may have legacy/pasted markup.
// Strip <script> elements and on*= handlers before innerHTML-ing.
function stripScripts(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ─── Variable display ──────────────────────────────
// Shows the live value of a page variable with optional label, prefix/
// suffix, and type-aware formatting. Mirrors the Angular variable
// component's formatValue (USD currency, locale number/date). Per-
// variable loading/error state isn't surfaced by the smart-page
// RuntimeContext, so this renders the resolved value or `emptyText`.

const VAR_TEXT_SIZE: Record<NonNullable<VariableBlock['textSize']>, string> = {
  sm: '0.875rem', md: '1.125rem', lg: '1.375rem', xl: '2rem',
}

function VariableBody({ block }: { block: VariableBlock }) {
  const runtime = useRuntime()
  const raw = block.name ? runtime.vars[block.name] : undefined
  const isEmpty = raw === undefined || raw === null || raw === ''
  const valueText = isEmpty
    ? (block.emptyText ?? '—')
    : `${block.prefix ?? ''}${formatVariableValue(raw, block.format)}${block.suffix ?? ''}`
  const items = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  const isJson = block.format === 'json'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: isJson ? 'stretch' : items, textAlign: block.align || 'left' }}>
      {block.label && (
        <span style={{
          fontSize: 12, color: 'var(--pg-text-secondary, var(--color-text-secondary))',
          textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500,
        }}>
          {block.label}
        </span>
      )}
      {isJson ? (
        // Multi-line, read-only data viewer (textarea-like) for JSON values.
        <pre style={{
          margin: 0, padding: 10, maxHeight: 320, overflow: 'auto',
          border: '1px solid var(--pg-border, var(--color-border))', borderRadius: 8,
          background: 'var(--pg-surface, var(--color-bg-secondary))',
          fontSize: '0.8rem', fontFamily: 'monospace',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: isEmpty ? 'var(--pg-text-secondary, var(--color-text-secondary))' : 'var(--pg-text, var(--color-text))',
        }}>{valueText}</pre>
      ) : (
        <span style={{
          fontSize: VAR_TEXT_SIZE[block.textSize ?? 'md'], fontWeight: 600,
          color: isEmpty ? 'var(--pg-text-secondary, var(--color-text-secondary))' : 'var(--pg-text, var(--color-text))',
        }}>
          {valueText}
        </span>
      )}
    </div>
  )
}

function formatVariableValue(v: unknown, fmt?: VariableDisplayFormat): string {
  if (v === null || v === undefined || v === '') return ''
  if (typeof v === 'object') {
    // Pretty-print for the json viewer; compact otherwise.
    try { return JSON.stringify(v, null, fmt === 'json' ? 2 : 0) } catch { return String(v) }
  }
  const str = String(v)
  switch (fmt) {
    case 'number': { const n = Number(str); return isFinite(n) ? n.toLocaleString() : str }
    case 'currency': { const n = Number(str); return isFinite(n) ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n) : str }
    case 'percentage': { const n = Number(str); return isFinite(n) ? `${n.toLocaleString()}%` : str }
    case 'date': { const d = new Date(str); return isNaN(d.getTime()) ? str : d.toLocaleDateString() }
    case 'datetime': { const d = new Date(str); return isNaN(d.getTime()) ? str : d.toLocaleString() }
    case 'time': { const d = new Date(str); return isNaN(d.getTime()) ? str : d.toLocaleTimeString() }
    case 'json': try { return JSON.stringify(JSON.parse(str), null, 2) } catch { return str }
    default: return str
  }
}

// Custom components (custom_code / custom_html) render through the SHARED
// CustomHtmlRenderer (page-runtime) — the same frame document, auto-height,
// theme and design-component support as every other surface. This host only
// supplies the capability-scoped `window.twasta` bridge, wired to this
// surface's vars / actions / data (parity with the standalone runtime).
function CustomCodeBody({ block }: { block: CustomCodeBlock }) {
  const runtime = useRuntime()
  const pageRuntime = usePageRuntime()
  const bridgeCaps = useMemo<CustomCodeBridgeCaps>(() => ({
    vars: runtime.vars,
    setVar: runtime.setVar,
    runAction: (name) => pageRuntime.runActionByName(name),
    navigate: (objType, objName) => pageRuntime.navigateTo(objType, objName),
    showMessage: (level, text) => pageRuntime.showMessage(level, text),
    getData: (nameOrId) => {
      // Data sources are addressed by their stable id ("ds_products") or the
      // bound transaction obj_name — there is no separate display name.
      const ds = (runtime.page.dataSources || []).find(
        (s) => s.id === nameOrId || s.resource?.obj_name === nameOrId,
      )
      const entry = runtime.resources[`ds:${ds?.id ?? nameOrId}`] as ResolvedDataEntry | undefined
      if (!entry) return null
      return entry.mode === 'record' ? (entry.row ?? null) : rowsFromEntry(entry)
    },
  }), [runtime.vars, runtime.setVar, runtime.resources, runtime.page.dataSources, pageRuntime])
  return (
    <CustomCodeBridgeHostContext.Provider value={bridgeCaps}>
      <CustomHtmlRenderer block={block} />
    </CustomCodeBridgeHostContext.Provider>
  )
}

// ─── Table (read-only) ─────────────────────────────
// Renders the authored static table. Cell/heading text supports {{var}}
// substitution. Editing affordances belong to the designer canvas, not
// the consumer runtime, so this is display-only.

function TableBody({ block }: { block: TableBlock }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const sub = (t?: string) => substituteVars(t, textVars)
  const cols = block.columns || []
  const rows = block.rows || []
  const wrapAlign = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  const cellPad = block.compact ? '6px 8px' : '10px 12px'
  const border = '1px solid var(--pg-border, var(--color-border, #e5e7eb))'
  const surface = 'var(--pg-surface, var(--color-surface, #f8fafc))'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: wrapAlign }}>
      {block.heading && <strong style={{ fontSize: 18, fontWeight: 600 }}>{sub(block.heading)}</strong>}
      {block.subheading && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--pg-text-secondary, var(--color-text-secondary))' }}>{sub(block.subheading)}</p>
      )}
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: block.compact ? 12 : 14, color: 'var(--pg-text, var(--color-text))' }}>
          {block.caption && (
            <caption style={{ captionSide: 'bottom', fontSize: 12, color: 'var(--pg-text-secondary, var(--color-text-secondary))', padding: 6 }}>
              {sub(block.caption)}
            </caption>
          )}
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.id} style={{
                  padding: cellPad, textAlign: c.align ?? 'left', width: c.width || undefined,
                  fontWeight: 600, background: surface,
                  borderBottom: border, border: block.bordered ? border : undefined,
                }}>
                  {sub(c.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={block.striped && ri % 2 === 0 ? { background: surface } : undefined}>
                {cols.map((c, ci) => (
                  <td key={c.id} style={{
                    padding: cellPad, textAlign: c.align ?? 'left', verticalAlign: 'top',
                    borderBottom: border, border: block.bordered ? border : undefined,
                  }}>
                    {sub(row?.[ci] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Input group ───────────────────────────────────
// Renders page-input controls bound to the runtime's page-vars store
// (same store embed selection writes into). React already has the page
// inputs in context, so no fetch is needed (unlike the Angular runtime).
// Resolves each named input, skipping `display` (read-only) and
// non-input variables. Mirrors the Angular input-group control set.

const SUFFIX_STYLE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', padding: '0 10px', fontSize: '0.78rem',
  background: 'var(--pg-surface, var(--color-surface))', border: '1px solid var(--pg-border, var(--color-border))',
  borderRadius: 6, color: 'var(--pg-text-secondary, var(--color-text-secondary))', minWidth: 36, justifyContent: 'center',
}

const FIELD_INPUT_STYLE: React.CSSProperties = {
  padding: '8px 12px', border: '1px solid var(--pg-border, var(--color-border))', borderRadius: 6,
  background: 'var(--pg-bg, var(--color-bg))', color: 'var(--pg-text, var(--color-text))',
  fontFamily: 'inherit', fontSize: '0.88rem', outline: 'none', width: '100%',
}

function InputGroupBody({ block }: { block: InputGroupBlock }) {
  const runtime = useRuntime()
  const byName = useMemo(() => {
    const m = new Map<string, PageInput>()
    for (const inp of runtime.page.inputs || []) m.set(inp.name, inp)
    return m
  }, [runtime.page.inputs])

  const resolved = (block.inputs || [])
    .map((n) => byName.get(n) ?? ({ name: n, label: n, type: 'text' } as PageInput))
    .filter((v) => !v.display && (v.isInput ?? true))

  const inline = (block.layout || 'stacked') === 'inline'

  if (resolved.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
        No inputs selected for this group.
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', flexDirection: inline ? 'row' : 'column',
      flexWrap: inline ? 'wrap' : undefined, alignItems: inline ? 'flex-end' : undefined, gap: 12,
    }}>
      {resolved.map((inp) => (
        <InputField
          key={inp.name}
          inp={inp}
          value={runtime.vars[inp.name]}
          onChange={(v) => runtime.setVar(inp.name, v)}
        />
      ))}
    </div>
  )
}

function htmlInputType(t: PageInput['type']): string {
  if (t === 'number') return 'number'
  if (t === 'date') return 'date'
  if (t === 'datetime') return 'datetime-local'
  if (t === 'time') return 'time'
  return 'text'
}

// Range / date_range values transport as a single "low|high" string.
function parseRangeValue(raw: unknown): [string, string] {
  const s = String(raw ?? '')
  if (!s) return ['', '']
  const [a, b] = s.split('|')
  return [a ?? '', b ?? '']
}

function InputField({ inp, value, onChange }: {
  inp: PageInput
  value: unknown
  onChange: (v: unknown) => void
}) {
  const { register } = usePageFieldRefs()
  const { errors, clearError } = usePageFieldErrors()
  const error = errors[inp.name]
  const cur = value ?? ''
  const [lo, hi] = parseRangeValue(value)

  // Register the focusable control so the action runner's `focus_input`
  // step can reach it; `register` is stable, so this ref is stable too.
  const labelRef = useCallback((el: HTMLLabelElement | null) => {
    const ctrl = el ? (el.querySelector('input, select, textarea') as HTMLElement | null) : null
    register(inp.name, ctrl)
  }, [register, inp.name])

  // Emit the change, and clear any action-set field error on edit.
  const emit = (v: unknown) => {
    onChange(v)
    if (error) clearError(inp.name)
  }

  let control: React.ReactNode
  switch (inp.type) {
    case 'checkbox':
      control = <input type="checkbox" checked={!!value} onChange={(e) => emit(e.currentTarget.checked)} />
      break
    case 'select':
      control = (
        <select value={String(cur)} onChange={(e) => emit(e.currentTarget.value)} style={FIELD_INPUT_STYLE}>
          <option value="" />
          {(inp.options || []).map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      )
      break
    case 'textarea':
      control = (
        <textarea
          value={String(cur)} rows={3} placeholder={inp.placeholder || ''}
          onChange={(e) => emit(e.currentTarget.value)}
          style={{ ...FIELD_INPUT_STYLE, resize: 'vertical' }}
        />
      )
      break
    case 'spinner':
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="number" min={inp.min} max={inp.max} step={inp.step ?? 1}
            value={String(cur)} onChange={(e) => emit(e.currentTarget.value)} style={FIELD_INPUT_STYLE} />
          {inp.unit && <span style={SUFFIX_STYLE}>{inp.unit}</span>}
        </div>
      )
      break
    case 'percentage':
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="number" min={inp.min ?? 0} max={inp.max ?? 100} step={inp.step ?? 1}
            value={String(cur)} onChange={(e) => emit(e.currentTarget.value)} style={FIELD_INPUT_STYLE} />
          <span style={SUFFIX_STYLE}>%</span>
        </div>
      )
      break
    case 'slider':
      control = (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="range" min={inp.min ?? 0} max={inp.max ?? 100} step={inp.step ?? 1}
            value={String(cur || (inp.min ?? 0))} onChange={(e) => emit(e.currentTarget.value)} style={{ flex: 1 }} />
          <span style={SUFFIX_STYLE}>{String(cur || (inp.min ?? 0))}{inp.unit ? ` ${inp.unit}` : ''}</span>
        </div>
      )
      break
    case 'range':
      control = (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input type="number" min={inp.min} max={inp.max} step={inp.step ?? 1} placeholder="From"
            value={lo} onChange={(e) => emit(`${e.currentTarget.value}|${hi}`)} style={FIELD_INPUT_STYLE} />
          <input type="number" min={inp.min} max={inp.max} step={inp.step ?? 1} placeholder="To"
            value={hi} onChange={(e) => emit(`${lo}|${e.currentTarget.value}`)} style={FIELD_INPUT_STYLE} />
        </div>
      )
      break
    case 'date_range':
      control = (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input type="date" value={lo} onChange={(e) => emit(`${e.currentTarget.value}|${hi}`)} style={FIELD_INPUT_STYLE} />
          <input type="date" value={hi} onChange={(e) => emit(`${lo}|${e.currentTarget.value}`)} style={FIELD_INPUT_STYLE} />
        </div>
      )
      break
    default:
      control = (
        <input
          type={htmlInputType(inp.type)} value={String(cur)} placeholder={inp.placeholder || ''}
          onChange={(e) => emit(e.currentTarget.value)} style={FIELD_INPUT_STYLE}
        />
      )
  }

  return (
    <label ref={labelRef} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--pg-text-secondary, var(--color-text-secondary))' }}>
        {inp.label || inp.name}
      </span>
      {control}
      {inp.helpText && !error && (
        <span style={{ fontSize: '0.75rem', color: 'var(--pg-text-secondary, var(--color-text-secondary))' }}>{inp.helpText}</span>
      )}
      {error && <span style={{ fontSize: '0.75rem', color: 'var(--pg-error, #dc2626)' }}>{error}</span>}
    </label>
  )
}

// ─── Generic form ──────────────────────────────────
// Renders the field set, validates required fields client-side, writes
// values into page vars, then dispatches the named submit action via the
// standard PageRuntime seam. (That seam is a no-op in the smart-page
// runtime until a live action runtime is wired in the next Phase 2 step;
// the form itself collects + validates regardless.)

function FormBody({ block }: { block: FormBlock }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const pageRuntime = usePageRuntime()
  const { register } = usePageFieldRefs()
  const { errors: ctxErrors, clearError: clearCtxError } = usePageFieldErrors()
  const sub = (t?: string) => substituteVars(t, textVars)

  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    const init: Record<string, string | boolean> = {}
    for (const f of block.fields || []) {
      if (f.type === 'checkbox') init[f.name] = false
      else if (f.defaultValue !== undefined) init[f.name] = f.defaultValue
    }
    return init
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const setField = (name: string, v: string | boolean) => {
    setValues((prev) => ({ ...prev, [name]: v }))
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors: Record<string, string> = {}
    for (const f of block.fields || []) {
      if (!f.required) continue
      const val = values[f.name]
      const empty = f.type === 'checkbox' ? !val : (val === undefined || String(val).trim() === '')
      if (empty) nextErrors[f.name] = `${f.label || f.name} is required`
    }
    if (Object.keys(nextErrors).length > 0) { setErrors(nextErrors); return }

    setErrors({})
    setSubmitting(true)
    try {
      // Surface field values as page vars so the submit action / bound
      // API can read them, then dispatch the named action.
      for (const f of block.fields || []) {
        if (f.name in values) runtime.setVar(f.name, values[f.name])
      }
      if (block.submitAction) await pageRuntime.runActionByName(block.submitAction)
      // The built-in store (and the block's server-side destination — an
      // email, a saved record). Default when no action is wired, exactly as
      // on the published site; this path used to stop at the action.
      const destination = block.destination ?? (block.submitAction ? 'action' : 'submissions')
      if (destination !== 'action' && pageRuntime.submitForm) {
        await pageRuntime.submitForm({
          block_id: block.id,
          form_label: block.heading || block.submitLabel,
          values: Object.fromEntries((block.fields || []).map((f) => [f.name, values[f.name] ?? ''])),
          honeypot_name: block.spam?.honeypotName || '_pg_hp',
          min_seconds: 0,
        })
        setDone(true)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Submission failed'
      setErrors((prev) => ({ ...prev, __form__: msg }))
    } finally {
      setSubmitting(false)
    }
  }

  const grid = block.layout === 'grid'
  const wrapAlign = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'stretch'

  if (done) {
    if (block.success?.redirectUrl) { window.location.href = block.success.redirectUrl; return null }
    return (
      <div role="status" style={{ padding: 16, border: '1px solid var(--pg-border, var(--color-border))', borderRadius: 8 }}>
        <strong>{sub(block.success?.title || 'Thank you')}</strong>
        <p style={{ margin: '6px 0 0' }}>{sub(block.success?.message || 'We have your submission.')}</p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', alignItems: wrapAlign }}
    >
      <div style={{
        display: 'grid', gap: 12, width: '100%',
        gridTemplateColumns: grid ? 'repeat(2, 1fr)' : '1fr',
      }}>
        {block.heading && (
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--pg-heading, inherit)', gridColumn: grid ? '1 / -1' : undefined }}>{sub(block.heading)}</h3>
        )}
        {block.subheading && (
          <p style={{ margin: '-6px 0 4px', color: 'var(--pg-text-secondary, var(--color-text-secondary))', fontSize: 13, gridColumn: grid ? '1 / -1' : undefined }}>
            {sub(block.subheading)}
          </p>
        )}

        {(block.fields || []).map((f) => {
          // Local required-validation error OR an error set by the submit
          // action via set_field_error (keyed by field name).
          const fieldError = errors[f.name] || ctxErrors[f.name]
          // Register the control so focus_input can reach it.
          const labelRef = (el: HTMLLabelElement | null) => {
            const ctrl = el ? (el.querySelector('input, select, textarea') as HTMLElement | null) : null
            register(f.name, ctrl)
          }
          return (
            <label key={f.id} ref={labelRef} style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--pg-text-secondary, var(--color-text-secondary))' }}>
                {f.label}{f.required && <span aria-hidden="true">&nbsp;*</span>}
              </span>
              <FormFieldControl
                field={f}
                value={values[f.name]}
                onChange={(v) => { setField(f.name, v); if (ctxErrors[f.name]) clearCtxError(f.name) }}
              />
              {f.helpText && !fieldError && <small style={{ fontSize: 12, color: 'var(--pg-text-secondary, var(--color-text-secondary))' }}>{f.helpText}</small>}
              {fieldError && <small style={{ fontSize: 12, color: 'var(--pg-error, #dc2626)' }}>{fieldError}</small>}
            </label>
          )
        })}

        {errors.__form__ && (
          <div style={{ gridColumn: grid ? '1 / -1' : undefined, fontSize: 12, color: 'var(--pg-error, #dc2626)' }}>
            {errors.__form__}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            justifySelf: 'start', gridColumn: grid ? '1 / -1' : undefined,
            padding: '10px 18px', fontSize: 14, fontWeight: 600,
            background: 'var(--pg-primary, var(--color-primary, #2563eb))', color: 'var(--pg-on-primary, #fff)',
            border: 'none', borderRadius: 'var(--pg-radius, 6px)',
            cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1,
          }}
        >
          {sub(block.submitLabel)}
        </button>
      </div>
    </form>
  )
}

function FormFieldControl({ field, value, onChange }: {
  field: import('@/pages/designers/page/types').FormFieldSpec
  value: string | boolean | undefined
  onChange: (v: string | boolean) => void
}) {
  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          name={field.name} required={field.required ?? false} rows={3}
          placeholder={field.placeholder ?? ''} value={String(value ?? '')}
          onChange={(e) => onChange(e.currentTarget.value)}
          style={{ ...FIELD_INPUT_STYLE, resize: 'vertical' }}
        />
      )
    case 'select':
      return (
        <select
          name={field.name} required={field.required ?? false} value={String(value ?? '')}
          onChange={(e) => onChange(e.currentTarget.value)} style={FIELD_INPUT_STYLE}
        >
          <option value="" />
          {(field.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
    case 'checkbox':
      return <input type="checkbox" name={field.name} checked={!!value} onChange={(e) => onChange(e.currentTarget.checked)} />
    case 'radio':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 400 }}>
              <input
                type="radio" name={field.name} value={o.value}
                checked={String(value ?? '') === o.value}
                onChange={(e) => onChange(e.currentTarget.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      )
    case 'file':
      return (
        <input
          type="file" name={field.name} required={field.required ?? false}
          onChange={(e) => onChange(e.currentTarget.files?.[0]?.name ?? '')}
          style={{ ...FIELD_INPUT_STYLE, padding: '6px 8px' }}
        />
      )
    default:
      return (
        <input
          type={field.type} name={field.name} required={field.required ?? false}
          placeholder={field.placeholder ?? ''} value={String(value ?? '')}
          onChange={(e) => onChange(e.currentTarget.value)} style={FIELD_INPUT_STYLE}
        />
      )
  }
}

// Container blocks that may have child blocks recurse through the host.
function ContainerWithChildren({ block }: { block: Block & { children?: Block[]; padding?: number; rounded?: boolean; align?: 'left' | 'center' | 'right'; textColor?: string; background?: import('@/pages/designers/page/types').Background; text?: string } }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  // Scope key matches the Angular runtime's `<block.id>/children`
  // convention so the persisted order map is portable.
  const scope = `${block.id}/children`
  const entries = useEntriesFor(scope, block.children || [])
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  const hasChildren = entries.length > 0
  const box = containerLayout(block as Pick<import("@twasta/page-schema").ContainerBlock, "minHeight" | "justify" | "maxWidth">)

  return (
    <section
      style={{
        ...(block.background ? backgroundForStyle(block.background) : {}),
        padding: block.padding ?? 24,
        borderRadius: block.rounded ? 12 : 0,
        textAlign: block.align || 'left',
        color: block.textColor || 'inherit',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.55,
        ...box.outer,
      }}
    >
      {hasChildren ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...box.inner }}>
          {runtime.reorganizeMode && (
            <AddBlockRow scope={scope} afterBlockId={null} parentBlock={block} currentOrder={entryIds} />
          )}
          {entries.map((entry) => (
            <React.Fragment key={entry.id}>
              <RuntimeBlockHost block={entry.block} scope={scope} />
              {runtime.reorganizeMode && (
                <AddBlockRow scope={scope} afterBlockId={entry.id} parentBlock={block} currentOrder={entryIds} />
              )}
            </React.Fragment>
          ))}
        </div>
      ) : block.text ? (
        substituteVars(block.text, textVars)
      ) : (
        <>
          <span style={{ opacity: 0.6 }}>Container</span>
          {runtime.canAddInto(block) && (
            <AddBlockRow scope={scope} afterBlockId={null} parentBlock={block} currentOrder={entryIds} />
          )}
        </>
      )}
    </section>
  )
}

// ── Accordion / tabs / columns with v2 child blocks ────────────────
// The shared designer renderers (AccordionRenderer, TabsRenderer,
// ColumnsRenderer) only render the legacy `body`/`text` field per
// item and ignore v2 `blocks[]`. Inside the live preview we need to
// dispatch the child blocks back through RuntimeBlockHost so any
// block type (including eCommerce) can render inside a container.

function AccordionWithChildren({ block }: { block: AccordionBlock }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const [openIds, setOpenIds] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    block.items.forEach((it) => { if (it.defaultOpen) init[it.id] = true })
    return init
  })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {block.items.map((it) => {
        const open = openIds[it.id]
        const hasChildren = Array.isArray(it.blocks) && it.blocks.length > 0
        return (
          <div key={it.id} style={{
            border: '1px solid var(--pg-border, var(--color-border))',
            borderRadius: 8,
            background: 'var(--pg-surface, var(--color-surface))',
            overflow: 'hidden',
          }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpenIds((p) => ({ ...p, [it.id]: !p[it.id] })) }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontWeight: 600, fontSize: '0.92rem',
                color: 'var(--pg-heading, var(--pg-text, var(--color-text)))',
                fontFamily: 'inherit',
              }}
            >
              <span>{substituteVars(it.title, textVars)}</span>
              <span style={{
                display: 'inline-block', transition: 'transform 0.15s',
                transform: open ? 'rotate(180deg)' : 'none',
                color: 'var(--pg-muted, var(--pg-text-secondary, var(--color-text-secondary)))',
              }}>▾</span>
            </button>
            {open && (
              <div style={{
                padding: hasChildren ? '4px 16px 14px' : '0 16px 14px',
                fontSize: '0.86rem',
                color: 'var(--pg-text, var(--color-text))', lineHeight: 1.55,
              }}>
                {hasChildren ? (
                  <NestedScope scope={`${block.id}/items/${it.id}`} children={it.blocks!} />
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{substituteVars(it.body || '', textVars)}</div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function TabsWithChildren({ block }: { block: TabsBlock }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  const [activeId, setActiveId] = useState<string>(block.items[0]?.id || '')
  const active = block.items.find((i) => i.id === activeId) || block.items[0]
  const variant = block.variant || 'underline'
  const overflow = block.overflow || 'wrap'
  const hasChildren = Array.isArray(active?.blocks) && active.blocks.length > 0

  return (
    <div>
      <div style={{
        display: 'flex', gap: variant === 'pill' ? 4 : 0,
        flexWrap: overflow === 'wrap' ? 'wrap' : 'nowrap',
        overflowX: overflow === 'scroll' ? 'auto' : 'visible',
        borderBottom: variant === 'underline' ? '1px solid var(--pg-border, var(--color-border))' : 'none',
        background: variant === 'pill' ? 'var(--pg-surface, var(--color-surface))' : 'transparent',
        padding: variant === 'pill' ? 4 : 0,
        borderRadius: variant === 'pill' ? 8 : 0,
        width: variant === 'pill' && overflow === 'wrap' ? 'fit-content' : '100%',
        maxWidth: '100%',
      }}>
        {block.items.map((it) => {
          const isActive = it.id === active?.id
          return (
            <button
              key={it.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveId(it.id) }}
              style={{
                padding: variant === 'pill' ? '6px 14px' : '10px 14px',
                background: variant === 'pill'
                  ? (isActive ? 'var(--pg-primary, var(--color-primary))' : 'transparent')
                  : 'transparent',
                color: variant === 'pill'
                  ? (isActive ? 'var(--pg-on-primary, #fff)' : 'var(--pg-muted, var(--pg-text-secondary, var(--color-text-secondary)))')
                  : (isActive ? 'var(--pg-primary, var(--color-primary))' : 'var(--pg-muted, var(--pg-text-secondary, var(--color-text-secondary)))'),
                fontWeight: isActive ? 700 : 500,
                border: 'none',
                borderRadius: variant === 'pill' ? 6 : 0,
                borderBottom: variant === 'underline'
                  ? (isActive ? '2px solid var(--pg-primary, var(--color-primary))' : '2px solid transparent')
                  : 'none',
                cursor: 'pointer', fontSize: '0.88rem',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                flexShrink: 0, whiteSpace: overflow === 'scroll' ? 'nowrap' : 'normal',
              }}
            >
              {substituteVars(it.label, textVars)}
            </button>
          )
        })}
      </div>
      <div style={{
        padding: '16px 4px', fontSize: '0.92rem',
        color: 'var(--pg-text, var(--color-text))', lineHeight: 1.6,
      }}>
        {hasChildren && active ? (
          <NestedScope scope={`${block.id}/items/${active.id}`} children={active.blocks!} />
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{substituteVars(active?.body || '', textVars)}</div>
        )}
      </div>
    </div>
  )
}

function ColumnsWithChildren({ block }: { block: ColumnsBlock }) {
  const runtime = useRuntime()
  const textVars = useVisibilityVars()  // page vars, me.*, data sources
  // `count` is the live column count; columns past it are kept in the model
  // (so lowering the count never deletes their blocks) but not rendered —
  // the same slice page-runtime's ColumnsRenderer and the canvas take.
  const cols = (Array.isArray(block.columns) ? block.columns : []).slice(0, block.count || undefined)
  // Per-column sizing (mirrors page-runtime ColumnsRenderer): explicit widths
  // opt out of the equal split; unsized columns → 1fr and share the slack.
  const tracks = cols.map((c) => columnTrack(c.width))
  const hasExplicit = tracks.some((t) => t !== null)
  const hasFill = tracks.some((t) => t === null)
  const justifyMap = { start: 'start', center: 'center', end: 'end', between: 'space-between' } as const
  // The same small-screen stacking every surface uses (page-runtime).
  const stack = columnsStackCss(block.id, { hasExplicit, count: cols.length, mobileOrder: block.mobileOrder, mobileLayout: block.mobileLayout, stretch: block.align === 'stretch' })
  return (
    <>
    <style dangerouslySetInnerHTML={{ __html: stack.css }} />
    <div className={stack.className} style={{
      display: 'grid',
      gridTemplateColumns: hasExplicit
        ? tracks.map((t) => t ?? '1fr').join(' ')
        : `repeat(${cols.length || block.count || 2}, 1fr)`,
      gap: block.gap ?? 16,
      alignItems:
        block.align === 'center' ? 'center'
        : block.align === 'bottom' ? 'end'
        : block.align === 'stretch' ? 'stretch'
        : 'start',
      ...(hasExplicit && !hasFill ? { justifyContent: justifyMap[block.justify || 'start'] } : null),
    }}>
      {cols.map((col) => {
        const hasChildren = Array.isArray(col.blocks) && col.blocks.length > 0
        return (
          <div key={col.id} style={{ minWidth: 0 }}>
            {hasChildren ? (
              <NestedScope scope={`${block.id}/columns/${col.id}`} children={col.blocks!} />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', color: 'var(--pg-text, var(--color-text))' }}>
                {substituteVars(col.text || '', textVars)}
              </div>
            )}
          </div>
        )
      })}
    </div>
    </>
  )
}

// Renders a list of child blocks under a stable scope key so the
// runtime's deletedIds / docOrder / added-blocks machinery still
// applies inside containers. Mirrors the body of ContainerWithChildren
// but without the section padding/background.
function NestedScope({
  scope, children,
}: { scope: string; children: Block[] }) {
  const runtime = useRuntime()
  const entries = useEntriesFor(scope, children)
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  if (entries.length === 0) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {runtime.reorganizeMode && (
        <AddBlockRow scope={scope} afterBlockId={null} parentBlock={null} currentOrder={entryIds} />
      )}
      {entries.map((entry) => (
        <React.Fragment key={entry.id}>
          <RuntimeBlockHost block={entry.block} scope={scope} />
          {runtime.reorganizeMode && (
            <AddBlockRow scope={scope} afterBlockId={entry.id} parentBlock={null} currentOrder={entryIds} />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

function backgroundForStyle(bg: import('@/pages/designers/page/types').Background) {
  if (!bg || bg.kind === 'none') return {}
  // Explicit transparent overrides any base surface fill (unlike `none`).
  if (bg.kind === 'transparent') return { background: 'transparent' }
  if (bg.kind === 'color') return { background: bg.color || 'var(--pg-surface)' }
  if (bg.kind === 'gradient') {
    const a = bg.gradientAngle ?? 135
    return { background: `linear-gradient(${a}deg, ${bg.gradientFrom || '#0f172a'} 0%, ${bg.gradientTo || '#1e3a8a'} 100%)` }
  }
  if (bg.kind === 'image' && bg.imageUrl) {
    const overlay = bg.overlay ? `linear-gradient(${bg.overlay}, ${bg.overlay}), ` : ''
    return { background: `${overlay}url(${bg.imageUrl}) center/cover no-repeat` }
  }
  return {}
}

// Re-export the PageVarsContext so the runtime composer can wire it.
export { PageVarsContext }
